import { z } from "zod";
import { idSchema } from "./common.ts";

/**
 * Floor plans. All geometry is in centimetres (real-world units, so generated rooms keep real walkways),
 * snapped to a 10 cm grid. x/y is the top-left corner before rotation.
 */
export const TABLE_SHAPES = ["square", "round", "rect", "booth", "bar"] as const;
export type TableShape = (typeof TABLE_SHAPES)[number];
export const FIXTURE_KINDS = ["bar", "wall", "door", "window", "plant", "label"] as const;
export type FixtureKind = (typeof FIXTURE_KINDS)[number];

export const GRID = 10;
const AISLE_X = 110; // chair + walkway between neighbouring tables
const AISLE_Y = 130; // between rows (chairs back to back plus a path)
const MARGIN = 80;

const key = z.string().min(1).max(64);
const cm = z.number().int().min(0).max(10_000);

export const floorAreaSchema = z.object({ key, id: idSchema.optional(), name: z.string().trim().min(1).max(40), width: z.number().int().min(200).max(10_000), height: z.number().int().min(200).max(10_000) });
export const floorTableSchema = z.object({
  key,
  id: idSchema.optional(),
  label: z.string().trim().min(1).max(40),
  areaKey: key.nullable(),
  shape: z.enum(TABLE_SHAPES),
  seats: z.number().int().min(1).max(30),
  x: cm,
  y: cm,
  w: z.number().int().min(30).max(2000),
  h: z.number().int().min(30).max(2000),
  rotation: z.number().int().min(0).max(359),
});
export const floorFixtureSchema = z.object({
  areaKey: key,
  kind: z.enum(FIXTURE_KINDS),
  x: cm,
  y: cm,
  w: z.number().int().min(10).max(10_000),
  h: z.number().int().min(10).max(10_000),
  rotation: z.number().int().min(0).max(359),
  label: z.string().trim().max(40).optional(),
});
export const floorDocumentSchema = z.object({
  areas: z.array(floorAreaSchema).max(12),
  tables: z.array(floorTableSchema).max(500),
  fixtures: z.array(floorFixtureSchema).max(500),
});
export type FloorArea = z.infer<typeof floorAreaSchema>;
export type FloorTable = z.infer<typeof floorTableSchema>;
export type FloorFixture = z.infer<typeof floorFixtureSchema>;
export type FloorDocument = z.infer<typeof floorDocumentSchema>;

export type TableSessionDto = { id: string; partySize: number | null; guestName: string | null; seatedAt: string };
export type FloorDto = {
  /** False while the location has no saved plan: the layout is generated from its tables. */
  saved: boolean;
  areas: Array<FloorArea & { id?: string }>;
  tables: Array<FloorTable & { id: string; isActive: boolean; session: TableSessionDto | null }>;
  fixtures: FloorFixture[];
};

/** Room description → layout spec (what the AI or the quick-start form produces). */
export const floorSpecSchema = z.object({
  areas: z
    .array(
      z.object({
        name: z.string().trim().min(1).max(40),
        tables: z.array(z.object({ count: z.number().int().min(1).max(60), seats: z.number().int().min(1).max(20), shape: z.enum(["square", "round", "rect", "booth"]) })).max(10),
        barSeats: z.number().int().min(0).max(30).default(0),
      }),
    )
    .min(1)
    .max(6),
});
export type FloorSpec = z.infer<typeof floorSpecSchema>;
export const describeFloorSchema = z.object({ text: z.string().trim().min(3).max(1000) });
export const seatTableSchema = z.object({ partySize: z.number().int().min(1).max(50).nullable().optional(), guestName: z.string().trim().max(60).optional() });
export const moveTableSchema = z.object({ toTableId: idSchema, merge: z.boolean().default(false) });

export const snap = (v: number) => Math.max(0, Math.round(v / GRID) * GRID);

/** Real-world footprint of a table (without chairs). */
export function tableSize(shape: TableShape, seats: number): { w: number; h: number } {
  switch (shape) {
    case "round": {
      const d = seats <= 2 ? 70 : seats <= 4 ? 100 : seats <= 6 ? 130 : seats <= 8 ? 160 : 180;
      return { w: d, h: d };
    }
    case "rect":
      return { w: Math.max(120, Math.ceil(seats / 2) * 60 + 20), h: 80 };
    case "booth":
      return { w: Math.max(120, Math.ceil(seats / 2) * 60 + 20), h: 140 };
    case "bar":
      return { w: Math.max(60, seats * 60), h: 50 };
    default: {
      const s = seats <= 2 ? 70 : seats <= 4 ? 90 : 120;
      return { w: s, h: s };
    }
  }
}

type Box = { w: number; h: number };
/** Greedy row packing: rows of tables with real aisles, centred, starting at (x0, y0). */
function packRows<T extends Box>(items: T[], maxRowWidth: number, x0: number, y0: number) {
  const rows: T[][] = [];
  let row: T[] = [];
  let width = 0;
  for (const it of items) {
    const add = (row.length ? AISLE_X : 0) + it.w;
    if (row.length && width + add > maxRowWidth) {
      rows.push(row);
      row = [];
      width = 0;
    }
    row.push(it);
    width += row.length > 1 ? AISLE_X + it.w : it.w;
  }
  if (row.length) rows.push(row);
  const rowWidth = (r: T[]) => r.reduce((s, i) => s + i.w, 0) + AISLE_X * (r.length - 1);
  const used = Math.max(0, ...rows.map(rowWidth));
  const placed: Array<T & { x: number; y: number }> = [];
  let y = y0;
  for (const r of rows) {
    const rh = Math.max(...r.map((i) => i.h));
    let x = x0 + (used - rowWidth(r)) / 2;
    for (const it of r) {
      placed.push({ ...it, x: snap(x), y: snap(y + (rh - it.h) / 2) });
      x += it.w + AISLE_X;
    }
    y += rh + AISLE_Y;
  }
  return { placed, width: used, height: rows.length ? y - AISLE_Y - y0 : 0 };
}

/** Row width that gives a pleasant, roughly 4:3 room for this set of tables. */
function targetWidth(items: Box[]) {
  const footprint = items.reduce((s, i) => s + (i.w + AISLE_X) * (i.h + AISLE_Y), 0);
  return Math.min(2600, Math.max(420, Math.sqrt(footprint * 1.4)));
}

function nextLabels(count: number, start: number, taken: Set<string>) {
  const out: string[] = [];
  for (let n = start; out.length < count; n++) if (!taken.has(String(n))) out.push(String(n));
  return out;
}

/**
 * Lay out a described room deterministically: tables grouped by size into centred rows with real
 * aisles, an optional bar counter with stools along the back wall, and the entrance at the front.
 */
export function arrangeFloor(spec: FloorSpec, opts: { startNumber?: number; takenLabels?: string[] } = {}): FloorDocument {
  const taken = new Set(opts.takenLabels ?? []);
  const doc: FloorDocument = { areas: [], tables: [], fixtures: [] };
  let number = opts.startNumber ?? 1;
  let bars = 0;
  spec.areas.forEach((a, ai) => {
    const areaKey = `area-${ai + 1}`;
    const items = a.tables
      .flatMap((g) => Array.from({ length: g.count }, () => ({ shape: g.shape as TableShape, seats: g.seats, ...tableSize(g.shape, g.seats) })))
      .sort((p, q) => p.seats - q.seats || p.shape.localeCompare(q.shape));
    const labels = nextLabels(items.length, number, taken);
    labels.forEach((l) => taken.add(l));
    number = Number(labels.at(-1) ?? number - 1) + 1;
    let top = MARGIN;
    const width = Math.max(targetWidth(items), a.barSeats ? a.barSeats * 60 + 120 : 0);
    if (a.barSeats) {
      const bw = a.barSeats * 60 + 60;
      doc.fixtures.push({ areaKey, kind: "bar", x: snap(MARGIN + (width - bw) / 2), y: snap(top), w: snap(bw), h: 60, rotation: 0 });
      bars++;
      const stools = tableSize("bar", a.barSeats);
      doc.tables.push({ key: `t-bar-${ai}`, label: bars > 1 ? `Bar ${bars}` : "Bar", areaKey, shape: "bar", seats: a.barSeats, x: snap(MARGIN + (width - stools.w) / 2), y: snap(top + 70), ...stools, rotation: 0 });
      top += 70 + stools.h + AISLE_Y;
    }
    const { placed, height } = packRows(items, width, MARGIN, top);
    placed.forEach((p, i) => doc.tables.push({ key: `t-${ai}-${i}`, label: labels[i]!, areaKey, shape: p.shape, seats: p.seats, x: p.x, y: p.y, w: p.w, h: p.h, rotation: 0 }));
    const areaW = snap(width + 2 * MARGIN);
    const areaH = snap(Math.max(top + height + MARGIN + 40, 300));
    doc.areas.push({ key: areaKey, name: a.name, width: areaW, height: areaH });
    doc.fixtures.push({ areaKey, kind: "door", x: snap(areaW / 2 - 45), y: areaH - 10, w: 90, h: 10, rotation: 0 });
  });
  return doc;
}

/** Turn an existing list of tables into a first floor plan (areas from label prefixes like "Terrace 3"). */
export function autoLayout(tables: Array<{ id: string; label: string; seats?: number; shape?: TableShape }>): FloorDocument {
  const groups = new Map<string, Array<{ id: string; label: string; n: number; seats: number; shape: TableShape }>>();
  for (const t of tables) {
    const m = t.label.trim().match(/^(.*?)[\s#-]*(\d+)$/);
    const prefix = m?.[1]?.trim() ?? "";
    const area = prefix && !/^(table|tisch|stol|tavolo|mesa|t)$/i.test(prefix) ? prefix : "Main room";
    groups.set(area, [...(groups.get(area) ?? []), { id: t.id, label: t.label, n: m ? Number(m[2]) : Number.MAX_SAFE_INTEGER, seats: t.seats ?? 4, shape: t.shape ?? "square" }]);
  }
  const doc: FloorDocument = { areas: [], tables: [], fixtures: [] };
  [...groups.entries()].forEach(([name, list], ai) => {
    const areaKey = `auto-${ai + 1}`;
    const items = list.sort((a, b) => a.n - b.n || a.label.localeCompare(b.label)).map((t) => ({ ...t, ...tableSize(t.shape, t.seats) }));
    const { placed, height, width } = packRows(items, targetWidth(items), MARGIN, MARGIN);
    for (const p of placed) doc.tables.push({ key: p.id, id: p.id, label: p.label, areaKey, shape: p.shape, seats: p.seats, x: p.x, y: p.y, w: p.w, h: p.h, rotation: 0 });
    const areaW = snap(Math.max(width, 300) + 2 * MARGIN);
    const areaH = snap(Math.max(height, 200) + 2 * MARGIN + 40);
    doc.areas.push({ key: areaKey, name, width: areaW, height: areaH });
    doc.fixtures.push({ areaKey, kind: "door", x: snap(areaW / 2 - 45), y: areaH - 10, w: 90, h: 10, rotation: 0 });
  });
  return doc;
}

/**
 * "Tidy up": group tables into rows by their vertical position, then align each row on one centre line
 * with even aisles, and space rows evenly. Keeps the order the owner sketched.
 */
export function tidyTables<T extends { x: number; y: number; w: number; h: number }>(tables: T[]): T[] {
  if (tables.length < 2) return tables.map((t) => ({ ...t, x: snap(t.x), y: snap(t.y) }));
  const sorted = [...tables].sort((a, b) => a.y + a.h / 2 - (b.y + b.h / 2));
  const rows: T[][] = [];
  for (const t of sorted) {
    const row = rows.at(-1);
    const cy = t.y + t.h / 2;
    if (row) {
      const rcy = row.reduce((s, r) => s + r.y + r.h / 2, 0) / row.length;
      const rh = Math.max(...row.map((r) => r.h), t.h);
      if (Math.abs(cy - rcy) < rh * 0.6) {
        row.push(t);
        continue;
      }
    }
    rows.push([t]);
  }
  const out = new Map<T, T>();
  let top = Math.min(...rows[0]!.map((t) => t.y));
  for (const row of rows) {
    const rh = Math.max(...row.map((t) => t.h));
    let x = Math.min(...row.map((t) => t.x));
    for (const t of [...row].sort((a, b) => a.x - b.x)) {
      out.set(t, { ...t, x: snap(x), y: snap(top + (rh - t.h) / 2) });
      x += t.w + AISLE_X;
    }
    top += rh + AISLE_Y;
  }
  return tables.map((t) => out.get(t)!);
}

const NUMBER_WORDS: Record<string, number> = { a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, fifteen: 15, twenty: 20 };
const num = (s: string | undefined) => (s == null ? undefined : /^\d+$/.test(s) ? Number(s) : NUMBER_WORDS[s.toLowerCase()]);
const OUTDOOR = /terrace|patio|garden|outside|outdoor|courtyard|rooftop/i;
const AREA_WORDS = /(inside|indoors?|main room|dining room|restaurant|terrace|patio|garden|outside|outdoors?|upstairs|downstairs|lounge|courtyard|rooftop|winter garden|room \d+)/i;

/**
 * Plain-text fallback for room descriptions when AI isn't available: understands phrases such as
 * "10 four-tops inside, terrace with 6 tables for 2 and 2 round tables for 6, bar with 8 stools".
 */
export function parseFloorText(text: string): FloorSpec | null {
  const areas: FloorSpec["areas"] = [];
  let current: FloorSpec["areas"][number] | null = null;
  const area = (name: string) => {
    const pretty = name.replace(/^indoors?$/i, "Inside").replace(/^outdoors?|outside$/i, "Outside");
    const n = pretty.charAt(0).toUpperCase() + pretty.slice(1).toLowerCase();
    current = areas.find((a) => a.name === n) ?? null;
    if (!current) areas.push((current = { name: n, tables: [], barSeats: 0 }));
    return current;
  };
  const W = "(\\d+|a|an|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|fifteen|twenty)";
  for (const clause of text.split(/[;,.\n]+|\band\b/i)) {
    const a = clause.match(AREA_WORDS);
    if (a) area(a[1]!);
    const bar = clause.match(new RegExp(`bar\\D{0,20}?${W}\\s*(?:stools|seats|chairs)|${W}\\s*(?:bar\\s*)?(?:stools|bar seats)`, "i"));
    if (bar) {
      // A bar mentioned without its own area is almost always indoors.
      const cur = current as FloorSpec["areas"][number] | null;
      const target = a && cur ? cur : (areas.find((x) => !OUTDOOR.test(x.name)) ?? cur ?? area("Main room"));
      target.barSeats = num(bar[1] ?? bar[2]) ?? 0;
      continue;
    }
    const re = new RegExp(`${W}\\s+(?:(round|square|rectangular|long|large|small)\\s+)?(?:${W}[- ]?tops?|(booths?)|tables?(?:\\s+(?:for|of|seating)\\s+${W})?)`, "gi");
    for (const m of clause.matchAll(re)) {
      const count = num(m[1]);
      if (!count) continue;
      const shapeWord = m[2]?.toLowerCase();
      const seats = num(m[3]) ?? num(m[5]) ?? (m[4] ? 4 : shapeWord === "small" ? 2 : shapeWord === "large" ? 6 : 4);
      const shape = m[4] ? "booth" : shapeWord === "round" ? "round" : shapeWord === "rectangular" || shapeWord === "long" || seats >= 6 ? "rect" : "square";
      (current ?? area("Main room")).tables.push({ count: Math.min(60, count), seats: Math.min(20, seats), shape });
    }
  }
  const useful = areas.filter((a) => a.tables.length || a.barSeats);
  return useful.length ? { areas: useful.slice(0, 6) } : null;
}
